import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { ArrowRight, Cpu, Network, Eye, BarChart3, Target, TrendingUp, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import BackNavigation from "@/components/BackNavigation";

// Cinematic visual assets - 4K UHD Human-Centered
const CINEMATIC_VISUALS = {
  heroVideo: "https://videos.pexels.com/video-files/3255275/3255275-uhd_2560_1440_25fps.mp4",
  heroPoster: "https://images.unsplash.com/photo-1708807472445-d33589e6b090?w=1920&q=80"
};

// System Architecture Diagram
const SYSTEM_ARCHITECTURE_DIAGRAM = "https://customer-assets.emergentagent.com/job_629d6fc3-18af-45e6-8912-76e3ef56678f/artifacts/8qd3p0bd_file_00000000a994722f8cd6bf78b8fea85f.png";

/**
 * Observatories Landing Page
 * The intelligence infrastructure of WAIT Theory
 * Contains ONLY 2 observatories: AI Observatory and Global Workforce Observatory
 */
export default function Observatories() {
  const observatories = [
    {
      id: "ai",
      icon: Cpu,
      title: "AI Observatory",
      tagline: "AI Capability Intelligence",
      description: "Identifies, organizes, evaluates, and standardizes AI capabilities for workforce integration across all Zodiac Lab applications.",
      functions: [
        "AI tool identification and cataloging",
        "Capability evaluation and standardization",
        "Zodiac AI Capability Registry management",
        "Lab AI Tool Library curation",
        "Integration support for labs"
      ],
      color: "#1E3A8A",
      link: "/observatories/ai",
      cta: "Explore AI Observatory",
      dashboardLink: "/observatories/ai/registry",
      dashboardLabel: "View AI Registry"
    },
    {
      id: "global",
      icon: Network,
      title: "Global Workforce Observatory",
      tagline: "Workforce Intelligence Measurement",
      description: "Measures workforce readiness, alignment, performance, and AI integration across individuals, organizations, and institutions worldwide.",
      functions: [
        "Workforce Intelligence Index (WII)",
        "Diagnostic assessments",
        "Benchmarking and reporting",
        "Industry analytics",
        "Regional workforce intelligence"
      ],
      color: "#991B1B",
      link: "/observatories/global",
      cta: "Explore Global Observatory",
      dashboardLink: "/observatories/workforce-dashboard",
      dashboardLabel: "View WII Dashboard"
    }
  ];

  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = 0.6;
      const handleLoaded = () => setVideoLoaded(true);
      video.addEventListener('loadeddata', handleLoaded);
      return () => video.removeEventListener('loadeddata', handleLoaded);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="observatories-page">
      {/* Back Navigation */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation />
      </div>

      {/* Hero Section - Cinematic Video Background */}
      <section className="relative overflow-hidden min-h-[70vh] flex items-center -mt-12">
        {/* Video Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000"
            style={{ 
              backgroundImage: `url(${CINEMATIC_VISUALS.heroPoster})`,
              opacity: videoLoaded ? 0 : 1
            }}
          />
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
            poster={CINEMATIC_VISUALS.heroPoster}
          >
            <source src={CINEMATIC_VISUALS.heroVideo} type="video/mp4" />
          </video>
          {/* Gradient Overlay - 30% opacity */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#991B1B]/35 via-[#7F1D1D]/30 to-[#991B1B]/25" />
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-medium mb-6">
              <Eye className="w-4 h-4" />
              Intelligence Infrastructure
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
              Observatories
            </h1>
            <p className="text-xl sm:text-2xl font-semibold text-white/90 mb-6">
              The Intelligence System of WAIT Theory
            </p>
            <p className="text-base sm:text-lg text-white/80 leading-relaxed mb-8 max-w-3xl">
              Two observatories provide the measurement, intelligence, and standardization infrastructure 
              that powers the entire Zodiac ecosystem. Together they enable data-driven workforce 
              intelligence development and AI integration.
            </p>
          </motion.div>
        </div>
      </section>

      {/* System Architecture Diagram Section */}
      <section className="py-10 lg:py-12 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-6"
          >
            <h2 className="font-heading text-xl sm:text-2xl font-bold mb-2" style={{ color: '#991B1B' }}>
              The Workforce Intelligence System Architecture
            </h2>
            <p className="text-sm text-[#1E3A8A]/80 max-w-xl mx-auto">
              This diagram illustrates how AI capability, workforce development, and measurement systems 
              are integrated within the Zodiac ecosystem.
            </p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white rounded-xl border border-[#E5E7EB] p-3 sm:p-4 shadow-md overflow-hidden"
          >
            <img 
              src={SYSTEM_ARCHITECTURE_DIAGRAM}
              alt="Workforce Intelligence System Architecture - showing AI Observatory, Zodiac Lab System (Workforce Lab, Organization Lab, Institutional Lab), and Workforce Observatory with feedback loop"
              className="max-w-full h-auto rounded-lg mx-auto object-contain"
              style={{ maxHeight: '320px' }}
              data-testid="system-architecture-diagram"
            />
            <p className="text-xs text-center text-[#1E3A8A]/50 mt-2">
              Figure 1: Zodiac Workforce Intelligence System Architecture
            </p>
          </motion.div>
        </div>
      </section>

      {/* Two Observatories */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Two Observatories. One Intelligence System.
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Each observatory serves a distinct function in the WAIT Theory infrastructure.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {observatories.map((obs, index) => (
              <motion.div
                key={obs.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="h-full"
              >
                <Card className="h-full border-[#E5E7EB] hover:shadow-xl transition-all overflow-hidden">
                  <div className="h-2" style={{ backgroundColor: obs.color }}></div>
                  <CardContent className="p-8">
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: `${obs.color}15` }}>
                      <obs.icon className="w-8 h-8" style={{ color: obs.color }} />
                    </div>
                    
                    <p className="text-sm font-medium mb-2" style={{ color: obs.color }}>{obs.tagline}</p>
                    <h3 className="font-heading text-2xl font-bold mb-3" style={{ color: '#991B1B' }}>
                      {obs.title}
                    </h3>
                    <p className="text-[#1E3A8A]/80 mb-6">{obs.description}</p>
                    
                    <div className="space-y-3 mb-8">
                      {obs.functions.map((func, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: obs.color }}></div>
                          <p className="text-sm text-[#1E3A8A]/70">{func}</p>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-3">
                      <Link to={obs.link} className="flex-1">
                        <Button 
                          className="w-full text-white"
                          style={{ backgroundColor: obs.color }}
                        >
                          {obs.cta}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                      <Link to={obs.dashboardLink}>
                        <Button 
                          variant="outline"
                          className="border-current"
                          style={{ color: obs.color, borderColor: obs.color }}
                        >
                          <Activity className="w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How They Work Together */}
      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Intelligence in Action
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              The observatories work together to power Zodiac Lab's intelligence-driven approach.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-[#E5E7EB] bg-white text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center mx-auto mb-4">
                    <BarChart3 className="w-6 h-6 text-[#1E3A8A]" />
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-2" style={{ color: '#991B1B' }}>
                    Measure
                  </h3>
                  <p className="text-sm text-[#1E3A8A]/70">
                    Global Workforce Observatory measures workforce intelligence through diagnostics and benchmarks
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-[#E5E7EB] bg-white text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-[#991B1B]/10 flex items-center justify-center mx-auto mb-4">
                    <Target className="w-6 h-6 text-[#991B1B]" />
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-2" style={{ color: '#991B1B' }}>
                    Align
                  </h3>
                  <p className="text-sm text-[#1E3A8A]/70">
                    AI Observatory identifies and standardizes AI capabilities for integration into development pathways
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-[#E5E7EB] bg-white text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-full bg-[#3B82F6]/10 flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-6 h-6 text-[#3B82F6]" />
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-2" style={{ color: '#991B1B' }}>
                    Develop
                  </h3>
                  <p className="text-sm text-[#1E3A8A]/70">
                    Zodiac Lab applies intelligence from both observatories to drive workforce development
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 overflow-hidden">
        {/* Background texture layer */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${CINEMATIC_VISUALS.heroPoster})`,
            opacity: 0.15
          }}
        />
        {/* Color overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A]/95 to-[#1E40AF]/95" />
        
        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Start With a Diagnostic
          </h2>
          <p className="text-white/80 mb-8">
            The Global Workforce Observatory diagnostic is your entry point to the Zodiac intelligence system.
          </p>
          <Link to="/observatories/global/survey">
            <Button size="lg" className="bg-white text-[#1E3A8A] hover:bg-white/90 h-14 px-8">
              Take the Diagnostic
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
